import { useCallback, useMemo, useState } from "react";
import {
  faCar,
  faFilePdf,
  faHashtag,
  faIdBadge,
  faMagnifyingGlass,
  faSpinner,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
} from "react-bootstrap";
import Select from "react-select";
import { useGetData } from "../../../hooks/useGetData";
import { ClientesInterface } from "../../../interfaces/ClientesInterface";
import { UsuarioInterface } from "../../../interfaces/UsuarioInterface";
import useFetch from "../../../hooks/useFetch";

//interfaces
import { ActividadBusqueda } from "../../../interfaces/ActividadBusqueda";
import { ColumnDef } from "@tanstack/react-table";
import CustomTable from "../../Formularios/CustomTable/CustomTable";
import TimelineModal from "../../Formularios/TimelineModal/TimelineModal";
import { Etapa } from "../../../interfaces/ActividadBusqueda";
import useDownloadPDF from "../../../hooks/useDownloadPDF";

interface formTextInputs {
  cliente_id: string | undefined;
  tecnico_id: string | undefined;
  actividad_id: string | undefined;
  placas: string | undefined;
  economico: string | undefined;
}

const BusquedaGeneral = () => {
  const { data: clientes } = useGetData("/cliente?estatus[eq]=1", "cliente");
  const { data: tecnicos } = useGetData("/usuario?rol_id[gt]?=1", "tecnicos");
  const [formTextInputs, setFormTextInputs] = useState<formTextInputs>({
    cliente_id: "",
    tecnico_id: "",
    actividad_id: "",
    placas: "",
    economico: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const { sendRequest } = useFetch();
  const [actividades, setActividades] = useState<ActividadBusqueda[]>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [actividadesEtapas, setActividadEtapas] = useState<Etapa[]>();
  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);
  const { mostrarPDF, mostrarPDFSinImagenes } = useDownloadPDF(setLoadingPDF);

  //Función que trata los servicios traidos de la BD.
  const transformData = useCallback((variable: any) => {
    setActividades(variable.data);
    setLoading(false);
  }, []);

  const findServices = () => {
    setLoading(true);
    sendRequest(
      {
        url: "/get_actividades",
        method: "POST",
        body: formTextInputs,
      },
      transformData
    );
  };

  const columns = useMemo<ColumnDef<ActividadBusqueda>[]>(
    () => [
      {
        header: "Órden de servicio",
        accessorKey: "id",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Razón social",
        accessorFn: (row) =>
          row.servicio.cliente.razon_social
            ? row.servicio.cliente.razon_social
            : "",
        id: "razon_social",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Folio",
        accessorFn: (row) => (row.servicio.folio ? row.servicio.folio : ""),
        id: "Folio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Tipo de servicio",
        accessorFn: (row) => row.tipo_servicio.nombre,
        id: "tipo_servicio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Económico",
        accessorFn: (row) => (row.unidad.economico ? row.unidad.economico : ""),
        id: "economico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "VIN",
        accessorFn: (row) => (row.unidad.vin ? row.unidad.vin : ""),
        id: "vin",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Placas",
        accessorFn: (row) => (row.unidad.placa ? row.unidad.placa : ""),
        id: "placa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Chapa",
        accessorFn: (row) => (row.chapa ? row.chapa.codigo : ""),
        id: "chapa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Técnico",
        accessorFn: (row) =>
          row.usuario
            ? `${row.usuario.nombre} ${row.usuario.apellido_paterno}`
            : "",
        id: "tecnico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Estatus",
        accessorFn: (row) => (
          <h5
            style={{ cursor: "pointer" }}
            onClick={() => {
              setShowModal(true);
              setActividadEtapas(row.etapa);
            }}
          >
            <Badge pill bg="secondary">
              {row.tipo_etapa.nombre}
            </Badge>
          </h5>
        ),
        id: "tipo_etapa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            {/** Boton de despliegue del PDF */}
            {info.row.original.etapa.find(
              (etapa) => etapa.tipo_etapa_id >= 3
            ) && (
              <div style={{ display: "flex", gap: "5px" }}>
                <Button
                  title="obtener el pdf de este servicio: UNO"
                  style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
                  disabled={loadingPDF}
                  onClick={() => mostrarPDF(info.row.original.id)}
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                </Button>
                <Button
                  title="Obtener el PDF sin imágenes de este servicio"
                  style={{ backgroundColor: "#85C1E9", borderColor: "white" }}
                  disabled={loadingPDF}
                  onClick={() => mostrarPDFSinImagenes(info.row.original.id)}
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                </Button>
              </div>
            )}
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [loadingPDF, mostrarPDF, mostrarPDFSinImagenes]
  );

  return (
    <Container>
      <h1>Búsqueda General</h1>
      {loadingPDF && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Creando PDF..."
          animated
          now={100}
        />
      )}
      <Form>
        <Row className="mb-3" style={{ textAlign: "left" }}>
          <InputGroup as={Col}>
            <InputGroup.Text id="btnGroupAddon">
              <FontAwesomeIcon icon={faHashtag} />
            </InputGroup.Text>
            <Form.Control
              type="number"
              step={1}
              min={1}
              placeholder="Órden de servicio"
              aria-label="Id del servicio"
              aria-describedby="btnGroupAddon"
              onChange={(event) =>
                setFormTextInputs((prev: any) => {
                  return { ...prev, actividad_id: event.target.value };
                })
              }
            />
          </InputGroup>
          <Form.Group as={Col}>
            <Select
              onChange={(value: any) => {
                setFormTextInputs((prev: any) => {
                  return { ...prev, cliente_id: value ? value.value : "" };
                });
              }}
              placeholder="Razón social"
              isClearable
              options={clientes?.map((cliente: ClientesInterface) => {
                return {
                  value: cliente.id,
                  label: cliente.razon_social,
                };
              })}
            />
          </Form.Group>
          <InputGroup as={Col}>
            <InputGroup.Text id="btnGroupAddon">
              <FontAwesomeIcon icon={faIdBadge} />
            </InputGroup.Text>
            <Select
              onChange={(value: any) => {
                setFormTextInputs((prev: any) => {
                  return { ...prev, tecnico_id: value ? value.value : "" };
                });
              }}
              placeholder="Técnico asignado"
              isClearable
              options={tecnicos?.map((tecnico: UsuarioInterface) => {
                return {
                  value: tecnico.id,
                  label: tecnico.nombre + " " + tecnico.apellido_paterno,
                };
              })}
            />
          </InputGroup>
        </Row>
        <hr />
        <Row className="mb-3" style={{ textAlign: "left" }}>
          <InputGroup as={Col}>
            <InputGroup.Text id="btnGroupAddon">
              <FontAwesomeIcon icon={faCar} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Placas"
              aria-label="Placas"
              aria-describedby="btnGroupAddon"
              onChange={(event) =>
                setFormTextInputs((prev: any) => {
                  return { ...prev, placas: event.target.value };
                })
              }
            />
          </InputGroup>
          <InputGroup as={Col}>
            <InputGroup.Text id="btnGroupAddon">
              <FontAwesomeIcon icon={faTags} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Económico"
              aria-label="Económico"
              aria-describedby="btnGroupAddon"
              onChange={(event) =>
                setFormTextInputs((prev: any) => {
                  return { ...prev, economico: event.target.value };
                })
              }
            />
          </InputGroup>
        </Row>
        <Button
          style={{
            float: "right",
            backgroundColor: "#21618C",
            marginBottom: "10px",
          }}
          title="Buscar"
          onClick={findServices}
          disabled={
            !Object.values(formTextInputs).find((element) => element !== "")
              ? true
              : false
          }
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} /> Buscar
        </Button>
        {loading && (
          <FontAwesomeIcon
            icon={faSpinner}
            pulse
            style={{ fontSize: "100", marginTop: 30 }}
          />
        )}
        {actividades && actividades?.length > 0 && (
          <CustomTable
            {...{
              data: actividades,
              columns,
            }}
          />
        )}
        {!loading && actividades && actividades?.length === 0 && (
          <h3 style={{ marginTop: "100px" }}>
            No se encontraron resultados con esos parámetros
          </h3>
        )}
      </Form>
      {/** Modal de cobro */}
      {showModal && (
        <TimelineModal
          actividadEtapas={actividadesEtapas}
          showModal={showModal}
          handleCloseModal={() => setShowModal(false)}
        />
      )}
    </Container>
  );
};

export default BusquedaGeneral;
